import React from 'react';
import { ArrowRight, Shield, FileText, Car, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { heroImages } from '../mockData';
import AnnouncementBar from './AnnouncementBar';
import { useCms } from '../hooks/useCms';

const HERO_DEFAULT = {
  badge: 'Cooperativa di Servizi Assicurativi',
  title: 'Proteggiamo la tua missione sociale',
  subtitle: 'Consulenza assicurativa etica e specializzata per Enti del Terzo Settore e Cooperative. Perché chi genera impatto positivo merita una protezione costruita sui propri valori.',
  cta1: 'Scheda di Quotazione',
  cta2: 'Prenota Consulenza',
};

const HeroSection = () => {
  const navigate = useNavigate();
  const cms = useCms('hero', HERO_DEFAULT);
  
  const scrollToForm = () => {
    const element = document.getElementById('contatti');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <section id="hero" className="relative min-h-[85vh] flex items-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImages[0]}
            alt="Consulenza Assicurativa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2380]/95 via-[#1B2380]/85 to-[#10154F]/95"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#0E8C84]/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1B2380]/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content - Centered */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="text-white animate-fade-in-up">
            <AnnouncementBar />
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <Shield size={18} className="text-[#8FE3DA]" />
              <span className="text-sm font-medium">{cms.badge}</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
              {cms.title}
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl lg:text-2xl mb-10 text-white/90 leading-relaxed max-w-3xl mx-auto">
              {cms.subtitle === HERO_DEFAULT.subtitle ? (
                <>
                  Consulenza assicurativa etica e specializzata per{' '}
                  <strong className="text-white font-semibold">Enti del Terzo Settore</strong> e{' '}
                  <strong className="text-white font-semibold">Cooperative</strong>. Perché chi genera impatto positivo merita una protezione costruita sui propri valori.
                </>
              ) : cms.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/quotazione')}
                data-testid="hero-quotazione-btn"
                className="group bg-[#0E8C84] hover:bg-[#0B7770] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <FileText size={20} />
                <span>{cms.cta1}</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </button>
              
              <button
                onClick={scrollToForm}
                data-testid="hero-contatti-btn"
                className="group bg-transparent border-2 border-white/80 hover:bg-white/10 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>{cms.cta2}</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Barra Servizi Rapidi */}
      <div data-testid="service-bar" className="bg-[#1a5c78] border-t border-[#0E8C84]/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <button
              onClick={() => navigate('/basta-la-targa')}
              data-testid="service-bar-rca-btn"
              className="group w-full sm:w-auto flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm hover:bg-[#E74C3C] text-white px-6 py-3.5 rounded-lg font-semibold text-base border border-white/20 hover:border-[#E74C3C] hover:shadow-xl transition-all duration-300"
            >
              <Car size={20} className="text-[#8FE3DA] group-hover:text-white transition-colors duration-300" />
              <span>Basta la Targa — Quotazione RCA</span>
              <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
            </button>

            <button
              onClick={() => navigate('/tirocini')}
              data-testid="service-bar-tirocini-btn"
              className="group w-full sm:w-auto flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm hover:bg-[#0E8C84] text-white px-6 py-3.5 rounded-lg font-semibold text-base border border-white/20 hover:border-[#0E8C84] hover:shadow-xl transition-all duration-300"
            >
              <Users size={20} className="text-[#8FE3DA] group-hover:text-white transition-colors duration-300" />
              <span>INCLUDIS — RC Tirocini</span>
              <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
